// Geometry and slide layout for the LED wall in the Leviathan stage photo.
//
// The wall's content is a flat slide (SLIDE_SIZE, slide px) mapped onto the
// photo (PHOTO_SIZE, photo px) by per-slice homographies stored in
// leviathanStageWall.geometry.json. scripts/leviathan-wall/measure-wall.mts
// measures the wall's edges in the photo and writes the file: 16 vertical
// slices whose homographies follow the measured top and bottom edge curves.
// The 2026 plate's top edge bows about 15 px from the corner chord.
//
// This module is pure data and math. It must not import React or touch
// browser globals: the measurement script runs it under `node --import tsx`.

import rawGeometry from "./leviathanStageWall.geometry.json";
import rawAssets from "./leviathanWallAssets.json";

export type Homography = number[][]; // 3x3, row-major, h22 = 1

export type WallGeometry = {
  image: { width: number; height: number };
  slide: { width: number; height: number };
  corners: {
    topLeft: [number, number];
    topRight: [number, number];
    bottomRight: [number, number];
    bottomLeft: [number, number];
  };
  slices: { u0: number; u1: number; homography: Homography }[];
};

export type WallSlice = {
  u0: number;
  u1: number;
  homography: Homography;
  matrix3d: string;
};

export type Placement = { scale: number; offsetX: number; offsetY: number };

/** One object-position axis: a fraction of the free space, or a px offset. */
export type ObjectPositionValue = number | { px: number };

function isPair(value: unknown): value is [number, number] {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    value.every((n) => typeof n === "number" && Number.isFinite(n))
  );
}

function isSize(value: unknown): value is { width: number; height: number } {
  if (typeof value !== "object" || value === null) return false;
  const size = value as { width?: unknown; height?: unknown };
  return (
    typeof size.width === "number" &&
    size.width > 0 &&
    typeof size.height === "number" &&
    size.height > 0
  );
}

function isHomography(value: unknown): value is Homography {
  return (
    Array.isArray(value) &&
    value.length === 3 &&
    value.every(
      (row) =>
        Array.isArray(row) &&
        row.length === 3 &&
        row.every((n) => typeof n === "number" && Number.isFinite(n))
    ) &&
    (value as Homography)[2]![2] === 1
  );
}

/**
 * Validates the geometry JSON shape. The file is written by hand today and by
 * the computer-vision curvature step later, so a clear failure beats a silent
 * NaN transform.
 */
export function assertWallGeometry(value: unknown): WallGeometry {
  const fail = (reason: string): never => {
    throw new Error(`leviathanStageWall.geometry.json is invalid: ${reason}`);
  };
  if (typeof value !== "object" || value === null) fail("not an object");
  const geometry = value as Partial<WallGeometry>;
  if (!isSize(geometry.image)) fail("image must be {width, height} > 0");
  if (!isSize(geometry.slide)) fail("slide must be {width, height} > 0");
  const corners = geometry.corners as Partial<WallGeometry["corners"]> | undefined;
  if (
    typeof corners !== "object" ||
    corners === null ||
    !isPair(corners.topLeft) ||
    !isPair(corners.topRight) ||
    !isPair(corners.bottomRight) ||
    !isPair(corners.bottomLeft)
  ) {
    fail("corners must hold topLeft/topRight/bottomRight/bottomLeft [x, y] pairs");
  }
  const slices = geometry.slices;
  if (!Array.isArray(slices) || slices.length === 0) fail("slices must be a non-empty array");
  for (const [index, slice] of slices!.entries()) {
    if (typeof slice !== "object" || slice === null) fail(`slices[${index}] is not an object`);
    const { u0, u1, homography } = slice as {
      u0?: unknown;
      u1?: unknown;
      homography?: unknown;
    };
    if (typeof u0 !== "number" || typeof u1 !== "number" || !(u0 >= 0) || !(u1 <= 1) || !(u0 < u1)) {
      fail(`slices[${index}] needs 0 <= u0 < u1 <= 1`);
    }
    if (!isHomography(homography)) {
      fail(`slices[${index}].homography must be a finite 3x3 matrix with h22 = 1`);
    }
  }
  return value as WallGeometry;
}

export const WALL_GEOMETRY: WallGeometry = assertWallGeometry(rawGeometry);

export const PHOTO_SIZE = WALL_GEOMETRY.image;
export const SLIDE_SIZE = WALL_GEOMETRY.slide;
export const WALL_CORNERS = WALL_GEOMETRY.corners;

/** Applies a slide-px-to-photo-px homography (h22 = 1) to one point. */
export function applyHomography(h: Homography, x: number, y: number): [number, number] {
  const [r0, r1, r2] = h as [number[], number[], number[]];
  const w = r2[0]! * x + r2[1]! * y + r2[2]!;
  return [
    (r0[0]! * x + r0[1]! * y + r0[2]!) / w,
    (r1[0]! * x + r1[1]! * y + r1[2]!) / w,
  ];
}

function solveLinear(matrix: number[][], rhs: number[]): number[] {
  const n = rhs.length;
  const m = matrix.map((row, i) => [...row, rhs[i]!]);
  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(m[row]![col]!) > Math.abs(m[pivot]![col]!)) pivot = row;
    }
    if (m[pivot]![col] === 0) throw new Error("homographyFromCorners: singular system");
    [m[col], m[pivot]] = [m[pivot]!, m[col]!];
    for (let row = 0; row < n; row++) {
      if (row === col) continue;
      const factor = m[row]![col]! / m[col]![col]!;
      for (let c = col; c <= n; c++) m[row]![c]! -= factor * m[col]![c]!;
    }
  }
  return m.map((row, i) => row[n]! / row[i]!);
}

/**
 * Direct linear transform: the homography with h22 = 1 mapping each
 * slideQuad[i] (slide px) to photoQuad[i] (photo px). Solves the 8x8 linear
 * system, one x and one y equation per corner pair.
 */
export function homographyFromCorners(
  slideQuad: [number, number][],
  photoQuad: [number, number][]
): Homography {
  if (slideQuad.length !== 4 || photoQuad.length !== 4) {
    throw new Error("homographyFromCorners: needs exactly four point pairs");
  }
  const a: number[][] = [];
  const b: number[] = [];
  for (let i = 0; i < 4; i++) {
    const [x, y] = slideQuad[i]!;
    const [px, py] = photoQuad[i]!;
    a.push([x, y, 1, 0, 0, 0, -x * px, -y * px]);
    b.push(px);
    a.push([0, 0, 0, x, y, 1, -x * py, -y * py]);
    b.push(py);
  }
  const h = solveLinear(a, b);
  return [
    [h[0]!, h[1]!, h[2]!],
    [h[3]!, h[4]!, h[5]!],
    [h[6]!, h[7]!, 1],
  ];
}

/**
 * CSS matrix3d for an element of SLIDE_SIZE px with transform-origin 0 0.
 * Column-major listing; the third row and column carry z untouched.
 */
export function matrix3dFromHomography(h: Homography): string {
  const [r0, r1, r2] = h as [number[], number[], number[]];
  const values = [
    r0[0], r1[0], 0, r2[0],
    r0[1], r1[1], 0, r2[1],
    0, 0, 1, 0,
    r0[2], r1[2], 0, r2[2],
  ];
  return `matrix3d(${values.join(", ")})`;
}

/**
 * The homography for a strip drawn at `scale` times slide px, mapping into
 * `scale` times photo px. The countdown no longer scales its whole 3840 px
 * frame down with a CSS transform: WebKit allocates a 3D layer's backing
 * store at the layer's CSS size times the pixel density squared, whatever
 * the transform then draws, so sixteen slide-sized strips cost about 2.4 GB
 * of graphics memory on an iPhone and its browser was killed. With the
 * scale folded into each strip's own coordinates, a strip's backing store is
 * its on-screen size. For H = [[A, b], [c, 1]] and L = scale * slide px,
 * scale * H(L / scale) = (A L + scale b) / ((c / scale) L + 1).
 */
export function scaleHomography(h: Homography, scale: number): Homography {
  const [r0, r1, r2] = h as [number[], number[], number[]];
  return [
    [r0[0]!, r0[1]!, r0[2]! * scale],
    [r1[0]!, r1[1]!, r1[2]! * scale],
    [r2[0]! / scale, r2[1]! / scale, 1],
  ];
}

/** All slices with their CSS transforms, left to right. */
export function wallSlices(): WallSlice[] {
  return WALL_GEOMETRY.slices.map((slice) => ({
    u0: slice.u0,
    u1: slice.u1,
    homography: slice.homography,
    matrix3d: matrix3dFromHomography(slice.homography),
  }));
}

/** Maps slide px to photo px using the slice that contains x. */
export function slideToPhoto(x: number, y: number): [number, number] {
  const slices = WALL_GEOMETRY.slices;
  const u = x / SLIDE_SIZE.width;
  const slice =
    slices.find((s) => u >= s.u0 && u < s.u1) ?? slices[slices.length - 1]!;
  return applyHomography(slice.homography, x, y);
}

/**
 * Reproduces CSS object-fit plus percentage object-position for the
 * PHOTO_SIZE image inside a container: scale the image by `scale`, then
 * translate by (offsetX, offsetY).
 */
export function fitPlacement(
  containerWidth: number,
  containerHeight: number,
  fit: "cover" | "contain",
  positionX: ObjectPositionValue = 0.5,
  positionY: ObjectPositionValue = 0.5
): Placement {
  const scaleX = containerWidth / PHOTO_SIZE.width;
  const scaleY = containerHeight / PHOTO_SIZE.height;
  const scale = fit === "cover" ? Math.max(scaleX, scaleY) : Math.min(scaleX, scaleY);
  // n === 0 also matches -0 (a negative slack times positionX 0).
  const zeroSafe = (n: number) => (n === 0 ? 0 : n);
  const offset = (free: number, position: ObjectPositionValue) => {
    if (typeof position === "number") return free * position;
    // A px length is the offset itself, a fraction px/free of the free
    // space. With zero free space the fraction is moot; use 0.
    return free === 0 ? 0 : position.px;
  };
  return {
    scale,
    offsetX: zeroSafe(offset(containerWidth - PHOTO_SIZE.width * scale, positionX)),
    offsetY: zeroSafe(offset(containerHeight - PHOTO_SIZE.height * scale, positionY)),
  };
}

/**
 * Parses a computed object-position, which browsers serialize as two values:
 * percentages like "62% 38%" (keywords are already serialized to
 * percentages) or resolved lengths like "12px 0%". Percentages become
 * position fractions; lengths become px offsets. Unparsable axes fall back
 * to 0.5.
 */
export function parseObjectPosition(
  value: string
): [ObjectPositionValue, ObjectPositionValue] {
  const tokens = value.trim().split(/\s+/);
  const parse = (token: string | undefined): ObjectPositionValue => {
    if (token) {
      if (token.endsWith("%")) {
        const n = Number(token.slice(0, -1));
        if (Number.isFinite(n)) return n / 100;
      } else if (token.endsWith("px")) {
        const n = Number(token.slice(0, -2));
        if (Number.isFinite(n)) return { px: n };
      }
    }
    return 0.5;
  };
  return [parse(tokens[0]), parse(tokens[1])];
}

// Lettering on the wall. Every word is an image: the image model drew the
// title, the ten digits, the four labels and the line on this wall's own blue
// (scripts/leviathan-wall/lettering-sheet-prompt.txt), and the sheet was cut
// into transparent sprites (key-lettering-sheet.py, recolor-lettering.py,
// encode-lettering-sprites.py), so every stroke carries the LED texture and
// the faint glow of the approved frame instead of a browser font's flat fill.
// leviathanWallAssets.json records each sprite's pixel size, solid-ink box
// and size variants. Placement is in slide px: a sprite is scaled so its ink height matches
// and its ink box is centered on (cx, cy), so a regenerated sprite with a
// different margin needs no layout change.
export type WallVariant = { src: string; width: number; height: number };

/**
 * One image the wall serves, in every size it is drawn at, encoded by
 * scripts/leviathan-wall/encode-2026-assets.mts: AVIF first (Chrome and
 * Safari 16.4 and later decode it), then WebP for cutouts or JPEG for the
 * photo. `width`, `height` and `ink` describe the largest fallback variant.
 */
export type WallAsset = {
  width: number;
  height: number;
  /** Solid-ink box inside the image, px: [x0, y0, x1, y1], x1 and y1 exclusive. Lettering only. */
  ink?: [number, number, number, number];
  avif: WallVariant[];
  fallback: WallVariant[];
  fallbackType: "image/webp" | "image/jpeg";
};

/** A lettering sprite: an asset with a solid-ink box, placed by that box. */
export type WallSprite = WallAsset & { ink: [number, number, number, number] };

export type WallSpriteBox = { left: number; top: number; width: number; height: number };

function isVariant(value: unknown): value is WallVariant {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Partial<WallVariant>;
  return typeof v.src === "string" && typeof v.width === "number" && v.width > 0 && typeof v.height === "number" && v.height > 0;
}

function isAsset(value: unknown): value is WallAsset {
  if (typeof value !== "object" || value === null) return false;
  const asset = value as Partial<WallAsset>;
  return (
    typeof asset.width === "number" &&
    typeof asset.height === "number" &&
    Array.isArray(asset.avif) &&
    asset.avif.every(isVariant) &&
    Array.isArray(asset.fallback) &&
    asset.fallback.length > 0 &&
    asset.fallback.every(isVariant) &&
    (asset.fallbackType === "image/webp" || asset.fallbackType === "image/jpeg") &&
    (asset.ink === undefined ||
      (Array.isArray(asset.ink) &&
        asset.ink.length === 4 &&
        asset.ink.every((n) => typeof n === "number" && Number.isFinite(n)) &&
        asset.ink[2]! > asset.ink[0]! &&
        asset.ink[3]! > asset.ink[1]!))
  );
}

export const WALL_LETTERING_NAMES = [
  "title", "line", "days", "hours", "minutes", "seconds",
  "digit-0", "digit-1", "digit-2", "digit-3", "digit-4",
  "digit-5", "digit-6", "digit-7", "digit-8", "digit-9",
] as const;

export const WALL_MASCOT_NAMES = ["punk", "pigeon", "cowboy", "yeti", "bunny"] as const;

export const WALL_ASSETS: Record<string, WallAsset> = (() => {
  const raw = rawAssets as Record<string, unknown>;
  const required = ["plate", "plate-blur", ...WALL_LETTERING_NAMES, ...WALL_MASCOT_NAMES.map((n) => `mascot-${n}`)];
  for (const name of required) {
    if (!isAsset(raw[name])) {
      throw new Error(`leviathanWallAssets.json is invalid: ${name} must be {width, height, avif, fallback, fallbackType}`);
    }
    if (WALL_LETTERING_NAMES.includes(name as (typeof WALL_LETTERING_NAMES)[number]) && !(raw[name] as WallAsset).ink) {
      throw new Error(`leviathanWallAssets.json is invalid: ${name} needs an ink box`);
    }
  }
  return raw as Record<string, WallAsset>;
})();

export function wallAsset(name: string): WallAsset {
  const asset = WALL_ASSETS[name];
  if (!asset) throw new Error(`no wall asset named ${name}`);
  return asset;
}

/** A srcset with width descriptors, so the browser picks the variant its `sizes` needs. */
export function wallSrcSet(variants: WallVariant[]): string {
  return variants.map((v) => `${v.src} ${v.width}w`).join(", ");
}

/**
 * Photo px per slide px across the wall, from the measured top edge; with
 * the frame's scale it gives an element's on-screen width for `sizes`.
 */
export const WALL_PHOTO_PX_PER_SLIDE_PX =
  (WALL_CORNERS.topRight[0] - WALL_CORNERS.topLeft[0]) / SLIDE_SIZE.width;

/**
 * The default `sizes` for an element `photoPx` photo px wide: the photo
 * covers a 16:9 frame, so its scale is the larger of width over 3840 and
 * height over 2160. A page that places the photo differently passes its own
 * rule through the component's `sizesForPhotoPx` prop.
 */
export function coverSizesForPhotoPx(photoPx: number): string {
  return `max(${(photoPx / 38.4).toFixed(2)}vw, ${(photoPx / 21.6).toFixed(2)}vh)`;
}

export function wallSprite(name: string): WallSprite {
  const asset = wallAsset(name);
  if (!asset.ink) throw new Error(`wall asset ${name} is not lettering`);
  return asset as WallSprite;
}

/** Scales a sprite so its ink is inkHeight slide px tall and centers the ink on (cx, cy). */
export function spriteBox(sprite: WallSprite, cx: number, cy: number, inkHeight: number): WallSpriteBox {
  const [x0, y0, x1, y1] = sprite.ink;
  const scale = inkHeight / (y1 - y0);
  return {
    left: cx - ((x0 + x1) / 2) * scale,
    top: cy - ((y0 + y1) / 2) * scale,
    width: sprite.width * scale,
    height: sprite.height * scale,
  };
}

export const WALL_TITLE_TEXT = "Project Leviathan";
export const WALL_LINE_TEXT = "While they\u2019re thinking, it\u2019s trending.";

// The block sits between the wall's left edge and the bull, about 6 percent
// in from the edge, so nothing runs off the wall in the phone crop. The
// sizes are the approved frame's, scaled up about 15 percent after Rohan
// found the lettering too small to read.
export const WALL_TITLE_PLACEMENT = { sprite: "title", cx: 1040, cy: 205, inkHeight: 205 };
export const WALL_LINE_PLACEMENT = { sprite: "line", cx: 1040, cy: 680, inkHeight: 82 };

/** Horizontal centers of the four digit pairs and their labels, slide px. */
export const WALL_NUMBER_CENTER_XS: number[] = [335, 805, 1275, 1745];

export const WALL_NUMBERS_LAYOUT = {
  xCenters: WALL_NUMBER_CENTER_XS,
  yCenters: [440, 440, 440, 440],
  y: 440,
  inkHeight: 125,
  /** Gap between glyphs of one pair, as a fraction of the ink height. */
  gapEm: 0.1,
};

export const WALL_LABELS_LAYOUT = {
  texts: ["DAYS", "HOURS", "MINUTES", "SECONDS"],
  sprites: ["days", "hours", "minutes", "seconds"],
  xCenters: WALL_NUMBER_CENTER_XS,
  y: 560,
  inkHeight: 34,
};

/** Boxes for the glyphs of one number, centered on cx, in reading order. */
export function digitGlyphBoxes(
  text: string,
  cx: number,
  cy: number,
  inkHeight: number,
  gapEm: number
): { digit: string; box: WallSpriteBox }[] {
  const glyphs = [...text].map((digit) => ({ digit, sprite: wallSprite(`digit-${digit}`) }));
  const widths = glyphs.map(
    ({ sprite }) => (sprite.ink[2] - sprite.ink[0]) * (inkHeight / (sprite.ink[3] - sprite.ink[1]))
  );
  const gap = gapEm * inkHeight;
  const total = widths.reduce((a, b) => a + b, 0) + gap * (glyphs.length - 1);
  let x = cx - total / 2;
  return glyphs.map(({ digit, sprite }, i) => {
    const w = widths[i]!;
    const box = spriteBox(sprite, x + w / 2, cy, inkHeight);
    x += w + gap;
    return { digit, box };
  });
}

// Visible boxes from layout-2026.json, shrunk to 90 percent toward each
// figure's feet so the bigger lettering clears their heads. The feet
// intentionally extend below the 1344 px wall. Contain and bottom center let
// replacement cutouts use the same boxes without depending on the source
// images' transparent margins.
function shrinkToFeet(box: { left: number; top: number; width: number; height: number }, factor: number) {
  const width = box.width * factor;
  const height = box.height * factor;
  return {
    left: box.left + box.width / 2 - width / 2,
    top: box.top + box.height - height,
    width,
    height,
  };
}

export const WALL_MASCOTS_LAYOUT = [
  { name: "punk", ...shrinkToFeet({ left: 76.606, top: 720.465, width: 446.066, height: 682.976 }, 0.9) },
  { name: "pigeon", ...shrinkToFeet({ left: 469.05, top: 982.925, width: 531.042, height: 424.576 }, 0.9) },
  { name: "cowboy", ...shrinkToFeet({ left: 923.019, top: 728.23, width: 461.505, height: 666.872 }, 0.9) },
  { name: "yeti", ...shrinkToFeet({ left: 1304.003, top: 765.573, width: 487.404, height: 635.969 }, 0.9) },
  { name: "bunny", ...shrinkToFeet({ left: 1723.316, top: 713.19, width: 460.43, height: 676.597 }, 0.9) },
] as const;
